import React, { useEffect, useState, useMemo, useCallback } from "react";
import instance from "../instance";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomerSearch from "./Customers/CustomerSearch";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal"; // تأكد من تثبيت مكتبة react-modal

const RequestsTable = () => {
  const [data, setData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [excludeTerm, setExcludeTerm] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null); // حالة لتتبع الطلب المفتوح

  const openModal = (request) => setSelectedRequest(request);
  const closeModal = () => setSelectedRequest(null);

  const [sortConfig, setSortConfig] = useState({
    key: "RequestTime",
    direction: "descending",
  });
  const [page, setPage] = useState(1);
  const [loadedPages, setLoadedPages] = useState(new Set());

  const fetchData = useCallback(async () => {
    if (loadedPages.has(page)) return;

    try {
      setLoading(true);
      const response = await instance.get(`/requests/`, {
        params: { page },
      });

      setData((prevData) =>
        page === 1
          ? response.data.data
          : [
              ...prevData,
              ...response.data.data.filter(
                (newItem) =>
                  !prevData.some(
                    (existingItem) => existingItem.ID === newItem.ID
                  )
              ),
            ]
      );

      setLoadedPages((prev) => new Set(prev).add(page));
      setHasMore(response.data.data.length > 0);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load data: " + error.message);
      setLoading(false);
      setHasMore(false);
    }
  }, [page, loadedPages]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (column) => {
    const direction =
      sortConfig.key === column && sortConfig.direction === "ascending"
        ? "descending"
        : "ascending";
    setSortConfig({ key: column, direction });
  };

  const handleSearch = (e) => setSearchTerm(e.target.value);
  const handleExcludeSearch = (e) => setExcludeTerm(e.target.value);
  const resetSearch = () => {
    setSearchTerm("");
    setExcludeTerm("");
  };

  const filteredAndSortedData = useMemo(() => {
    let filteredData = data;

    if (searchTerm) {
      filteredData = filteredData.filter((item) =>
        Object.values(item).some((value) =>
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (excludeTerm) {
      filteredData = filteredData.filter(
        (item) =>
          !Object.values(item).some((value) =>
            value?.toString().toLowerCase().includes(excludeTerm.toLowerCase())
          )
      );
    }

    return filteredData.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
  }, [data, searchTerm, excludeTerm, sortConfig]);

  const loadMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const getDateCellColor = (dateString) => {
    if (!dateString) return "transparent";

    const now = new Date();
    const currentDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const date = new Date(dateString);
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const past6Days = new Date(currentDate.getTime() - 6 * 24 * 60 * 60 * 1000);

    if (dateOnly.getTime() === currentDate.getTime()) {
      return "rgba(8, 171, 14, 0.3)";
    } else if (dateOnly < past6Days) {
      return "rgba(255, 0, 0, 0.2)";
    } else {
      return "transparent";
    }
  };

  const formatDateToCustomString = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getAllUniqueValues = () => {
    const uniqueValues = new Set();
    data.forEach((item) => {
      Object.values(item).forEach((value) => {
        if (value instanceof Date || !isNaN(Date.parse(value))) {
          uniqueValues.add(formatDateToCustomString(value));
        } else {
          uniqueValues.add(value);
        }
      });
    });
    return Array.from(uniqueValues).sort((a, b) => (a > b ? -1 : 1));
  };

  const getRequestTypeColor = (type) => {
    switch (type) {
      case "Technical Support":
        return "rgba(255, 217, 0, 0.3)";
      case "Feature Request":
        return "rgba(8, 171, 14, 0.3)";
      case "Bug Report":
        return "rgba(255, 0, 0, 0.2)";
      case "General Inquiry":
        return "rgba(192, 192, 192, 0.3)";
      default:
        return "transparent";
    }
  };

  const getApprovalStatusColor = (type) => {
    switch (type) {
      case "Pending":
        return "rgba(255, 217, 0, 0.3)";
      case "Approved":
        return "rgba(8, 171, 14, 0.3)";
      case "Rejected":
        return "rgba(255, 0, 0, 0.2)";
      case "Canceled":
        return "rgba(192, 192, 192, 0.3)";
      default:
        return "transparent";
    }
  };

  const getPrivacyColor = (privacy) => {
    return privacy === true ? "rgba(8, 171, 14, 0.3)" : "transparent";
  };

  if (loading && page === 1) return <div>Loading...</div>;

  return (
    <div>
      <header>
        <h1>Support Requests</h1>
      </header>
      <button onClick={() => navigate("/")} className="button">
        ← Main
      </button>
      <button
        onClick={() => (window.location.href = "/NewRequest")}
        className="button"
      >
        <i className="fas fa-plus"></i>
      </button>

      <CustomerSearch
        searchTerm={searchTerm}
        excludeTerm={excludeTerm}
        datalistData={getAllUniqueValues()}
        handleSearch={handleSearch}
        handleExcludeSearch={handleExcludeSearch}
        resetSearch={resetSearch}
        dateFilter={null}
        formatDateRange={() => ""}
      />

      <table>
        <thead>
          <tr>
            <th className="sortable" onClick={() => handleSort("ID")}>
              ID
            </th>
            <th className="sortable" onClick={() => handleSort("Code")}>
              Code
            </th>
            <th className="sortable" onClick={() => handleSort("SystemName")}>
              System Name
            </th>
            <th className="sortable" onClick={() => handleSort("Governorate")}>
              Governorate
            </th>
            <th className="sortable" onClick={() => handleSort("City")}>
              City
            </th>
            <th className="sortable" onClick={() => handleSort("Phone")}>
              Phone
            </th>
            <th className="sortable" onClick={() => handleSort("RequestType")}>
              Request Type
            </th>
            <th>Details</th>
            <th
              className="sortable"
              onClick={() => handleSort("RequestNumber")}
            >
              Request Number
            </th>
            <th className="sortable" onClick={() => handleSort("RequestTime")}>
              Request Time
            </th>
            <th
              className="sortable"
              onClick={() => handleSort("ApprovalStatus")}
            >
              Approval Status
            </th>
            <th className="sortable" onClick={() => handleSort("Privacy")}>
              Privacy
            </th>
            <th className="sortable" onClick={() => handleSort("UserID")}>
              User
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedData.map((item) => (
            <tr key={item.ID}>
              <td>{item.ID}</td>
              <td>{item.Name || ""}</td>
              <td>{item.SystemName}</td>
              <td>{item.Governorate}</td>
              <td>{item.City}</td>
              <td>{item.Phone}</td>
              <td
                style={{
                  backgroundColor: getRequestTypeColor(item.RequestType),
                }}
              >
                {item.RequestType}
              </td>
              <td>
                <button
                  onClick={() => openModal(item)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <i className="fas fa-info-circle"></i>
                </button>
              </td>
              <td>{item.RequestNumber}</td>
              <td
                style={{ backgroundColor: getDateCellColor(item.RequestTime) }}
              >
                {new Date(item.RequestTime).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })}
              </td>
              <td
                style={{
                  backgroundColor: getApprovalStatusColor(item.ApprovalStatus),
                }}
              >
                {" "}
                {item.ApprovalStatus}
              </td>
              <td
                style={{
                  backgroundColor: getPrivacyColor(item.Privacy),
                }}
              >
                {item.Privacy === true
                  ? "Yes"
                  : item.Privacy === false
                  ? "No"
                  : "Unknown"}
              </td>
              <td>{item.Username || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Modal
        isOpen={!!selectedRequest}
        onRequestClose={closeModal}
        contentLabel="Request Details"
        style={{
          content: {
            maxWidth: "500px",
            margin: "auto",
            wordWrap: "break-word",
          },
        }}
      >
        {selectedRequest && (
          <div>
            <h2>Request Details</h2>
            <h4>
              <strong>Description:</strong> {selectedRequest.RequestDescription}
            </h4>
            <h4>
              <strong>Attachments:</strong>{" "}
              {selectedRequest.Attachments || "No attachments"}
            </h4>
            <button onClick={closeModal} className="button">
              Close
            </button>
          </div>
        )}
      </Modal>
      {hasMore && (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <button onClick={loadMore} className="button">
            Load More
          </button>
        </div>
      )}
      {loading && page > 1 && <div>Loading more...</div>}
      <footer>
        <span>© 2025 OwlTech All rights reserved.</span>
      </footer>
    </div>
  );
};

export default RequestsTable;
